import React, { Fragment, ReactElement } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Tooltip } from '@material-ui/core'

export const useTooltipBoxStyles = makeStyles((theme) => ({
  tooltip: {
    cursor: 'pointer',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.text.default,
    maxWidth: 'none',
    border: `1px solid ${theme.palette.background.grey}`,
    boxShadow: theme.shadows[1],
    fontSize: 16,
  },
  tooltipDark: {
    backgroundColor: theme.palette.background.focus,
    color: theme.palette.text.invert,
    border: `1px solid ${theme.palette.background.greyDark}`,
  },
  popper: {
    width: '100%',
    maxWidth: theme.breakpoints.values['md'],
  },
}))

export type TooltipBoxVariant = 'primary' | 'dark'

export type TooltipBoxProps = RvG.IReactDefaultProps & {
  content: ReactElement | string
  variant?: TooltipBoxVariant
}

export function getTooltipBoxTooltipClass(
  classes: ReturnType<typeof useTooltipBoxStyles>,
  variant: TooltipBoxVariant = 'primary'
): string {
  return variant === 'dark'
    ? `${classes.tooltip} ${classes.tooltipDark}`
    : classes.tooltip
}

export default function TooltipBox({
  className,
  children,
  content,
  variant = 'primary',
  ...props
}: TooltipBoxProps): ReactElement {
  const classes = useTooltipBoxStyles()
  const TooltipInner = React.forwardRef<HTMLDivElement>(function TooltipInner(
    props,
    ref
  ) {
    //  Spread the props to the underlying DOM element.
    return (
      <div className={className} {...props} ref={ref}>
        {children}
      </div>
    )
  })

  function renderTooltipContent() {
    return <Fragment>{content}</Fragment>
  }

  return (
    <Tooltip
      data-testid={'tooltip'}
      title={renderTooltipContent()}
      classes={{
        tooltip: getTooltipBoxTooltipClass(classes, variant),
        popper: classes.popper,
      }}
      {...props}
    >
      <TooltipInner />
    </Tooltip>
  )
}
